import React from "react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import { ChevronDown, Download, FileSpreadsheet, FileText } from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { useExportSyncJobs } from "../hooks/pms.hooks";
import { SyncJobsExportFormat, SyncJobsFilters } from "@/types/pms";

interface ExportSyncJobsButtonProps {
  filters: SyncJobsFilters;
  disabled?: boolean;
}

const downloadBlob = (blob: Blob, filename: string) => {
  const url = URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.download = filename;
  link.style.visibility = "hidden";
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
};

const buildFilename = (format: SyncJobsExportFormat): string => {
  const stamp = new Date().toISOString().slice(0, 10);
  return `pms-sync-jobs-${stamp}.${format}`;
};

export const ExportSyncJobsButton: React.FC<ExportSyncJobsButtonProps> = ({
  filters,
  disabled,
}) => {
  const { t } = useTranslation();
  const { mutate: exportJobs, isPending } = useExportSyncJobs();

  const handleExport = (format: SyncJobsExportFormat) => {
    exportJobs(
      { ...filters, format },
      {
        onSuccess: (blob) => {
          downloadBlob(blob, buildFilename(format));
          toast.success(t("pms.sync.export.success"));
        },
        onError: () => {
          toast.error(t("pms.sync.export.error"));
        },
      },
    );
  };

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button variant="new_outline" disabled={disabled || isPending}>
          <Download className="size-4" />
          {isPending
            ? t("pms.sync.export.exporting")
            : t("pms.sync.export.label")}
          <ChevronDown className="size-4" />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end">
        <DropdownMenuItem onClick={() => handleExport("xlsx")}>
          <FileSpreadsheet className="size-4" />
          {t("pms.sync.export.xlsx")}
        </DropdownMenuItem>
        <DropdownMenuItem onClick={() => handleExport("csv")}>
          <FileText className="size-4" />
          {t("pms.sync.export.csv")}
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
};
